import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Home,
  Layers,
  Wheat,
  Tag,
  Scale,
  BookOpen,
  BarChart3,
  CheckSquare,
  Database,
  Users,
  FolderLock,
  ChevronLeft,
  ChevronRight,
  Bug,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useBugReport } from '@/context/BugReportContext';
import { cn } from '@/lib/utils';
import { NavItem } from '@/types/navigation';

export const DesktopSidebar: React.FC = () => {
  const { isAdmin } = useAuth();
  const { openBugReport } = useBugReport();
  const [collapsed, setCollapsed] = useState<boolean>(false);

  const primaryNav: NavItem[] = [
    { name: 'Home', href: '/', icon: Home, exact: true },
    { name: 'Production Cycles', href: '/cycles', icon: Layers },
    { name: 'Feed Management', href: '/feed', icon: Wheat },
    { name: 'Pig Sales & Scale', href: '/sales', icon: Tag },
    { name: 'Grower Settlements', href: '/settlements', icon: Scale },
    { name: 'General Ledger', href: '/ledger', icon: BookOpen },
    { name: 'Reports & Analytics', href: '/reports', icon: BarChart3 },
    { name: 'Documents', href: '/documents', icon: FolderLock },
  ];

  const adminNav: NavItem[] = [
    { name: 'Bug Reports', href: '/admin/bugs', icon: Bug },
    { name: 'Review & Resolve', href: '/admin/review-and-resolve', icon: CheckSquare },
    { name: 'Master Tables', href: '/admin/master-data', icon: Database },
    { name: 'Users & Security', href: '/admin/users', icon: Users },
  ];

  return (
    <aside
      className={cn(
        'hidden lg:flex flex-col bg-slate-900 text-slate-200 transition-all duration-300 ease-in-out border-r border-slate-800 z-30 h-screen sticky top-0',
        collapsed ? 'w-18' : 'w-60'
      )}
    >
      {/* Brand Header */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800 flex-shrink-0">
        <div className="flex items-center gap-3 overflow-hidden">
          <div className="w-9 h-9 rounded-lg bg-emerald-700 flex items-center justify-center text-white flex-shrink-0 shadow-sm font-bold text-lg">
            🐷
          </div>
          {!collapsed && (
            <div className="truncate">
              <h1 className="text-sm font-bold tracking-tight text-white truncate">VSB Farm</h1>
              <p className="text-[10px] text-emerald-400 font-medium tracking-wider uppercase">Management</p>
            </div>
          )}
        </div>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Navigation Links */}
      <div className="flex-1 overflow-y-auto py-4 px-3 space-y-6 no-scrollbar">
        {/* Operations Section */}
        <div>
          {!collapsed && (
            <p className="px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Operations
            </p>
          )}
          <nav className="space-y-1">
            {primaryNav.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                end={item.exact}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors group',
                    isActive
                      ? 'bg-emerald-700 text-white font-semibold shadow-sm'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white',
                    collapsed && 'justify-center px-2'
                  )
                }
                title={collapsed ? item.name : undefined}
              >
                <item.icon className={cn('w-4 h-4 flex-shrink-0')} />
                {!collapsed && <span className="truncate">{item.name}</span>}
              </NavLink>
            ))}
          </nav>
        </div>

        {/* Administration Section (Admin only) */}
        {isAdmin && (
          <div>
            {!collapsed && (
              <p className="px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Administration
              </p>
            )}
            <nav className="space-y-1">
              {adminNav.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors group',
                      isActive
                        ? 'bg-emerald-700 text-white font-semibold shadow-sm'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white',
                      collapsed && 'justify-center px-2'
                    )
                  }
                  title={collapsed ? item.name : undefined}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {!collapsed && <span className="truncate">{item.name}</span>}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </div>

      {/* Footer / Build badge & Bug trigger */}
      {!collapsed ? (
        <div className="p-3 border-t border-slate-800 text-[10px] text-slate-400 flex items-center justify-between">
          <button
            onClick={() => openBugReport()}
            className="hover:text-red-400 flex items-center gap-1 transition-colors"
            title="Report a bug"
          >
            <Bug className="w-3 h-3 text-red-400" />
            <span>Report issue</span>
          </button>
          <span className="inline-flex items-center gap-1 text-emerald-400">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live DB
          </span>
        </div>
      ) : (
        <div className="p-2 border-t border-slate-800 flex justify-center">
          <button
            onClick={() => openBugReport()}
            className="p-1.5 rounded hover:bg-slate-800 text-red-400 hover:text-red-300 transition-colors"
            title="Report a bug"
          >
            <Bug className="w-4 h-4" />
          </button>
        </div>
      )}
    </aside>
  );
};

